import {
  Control,
  Controller,
  FieldValues,
  get,
  Path,
  useFormState,
} from 'react-hook-form';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { type LabelValue } from '@/types/global';
import clsx from 'clsx';
import { useTranslation } from '@/i18n';

interface StyledRadioGroupProps<FieldsType extends FieldValues> {
  control: Control<FieldsType>;
  name: Path<FieldsType>;
  options: Array<LabelValue<string>>;
  className?: React.HTMLAttributes<HTMLDivElement>['className'];
  displayInColumns?: boolean;
  /** Oui/Non on one horizontal line (e.g. label left, radios right). */
  inline?: boolean;
  disabled?: boolean;
  customOnChange?: (value: string) => void;
}

const StyledRadioGroup = <FieldsType extends FieldValues>({
  control,
  className,
  name,
  options,
  displayInColumns = false,
  inline = false,
  disabled = false,
  customOnChange,
}: StyledRadioGroupProps<FieldsType>) => {
  const { errors } = useFormState({ control });
  const errorMessage = get(errors, name + '.message') as unknown as string;
  return (
    <div className={clsx(className, { 'text-error-500': errorMessage })}>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <RadioGroup
            className={clsx('flex w-full', {
              'flex-row items-center gap-4 w-auto': inline,
              'flex-col': !inline && displayInColumns,
              'flex-col md:flex-row': !inline && !displayInColumns,
            })}
            value={field.value}
          >
            {options.map(({ label, value }) => (
              <div
                key={`radio-${value}`}
                className={clsx('flex items-center space-x-2', {
                  'h-10': inline,
                  'w-full': !inline,
                  'w-auto shrink-0': inline,
                })}
              >
                <RadioGroupItem
                  id={`${name}-${value}`}
                  {...field}
                  value={value}
                  onClick={() => {
                    field.onChange(value);
                    if (customOnChange) customOnChange(value);
                  }}
                  disabled={disabled}
                />
                <Label className="text-gray-700" htmlFor={`${name}-${value}`}>
                  {label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        )}
      />
      {errorMessage !== '' && (
        <p className="text-sm leading-6 font-medium text-error-500">
          {errorMessage}
        </p>
      )}
    </div>
  );
};

type YesNoStyledRadioGroupProps<FieldsType extends FieldValues> = Omit<
  StyledRadioGroupProps<FieldsType>,
  'options'
>;

export const YesNoStyledRadioGroup = <FieldsType extends FieldValues>({
  inline,
  ...props
}: YesNoStyledRadioGroupProps<FieldsType>) => {
  const { t } = useTranslation();
  return (
    <StyledRadioGroup
      {...props}
      inline={inline}
      options={[
        { label: t('common.yes'), value: 'true' },
        { label: t('common.no'), value: 'false' },
      ]}
    />
  );
};

export default StyledRadioGroup;
